import { mapDeviceAuthenticationFromSysApi } from '@shure/shared/angular/data-access/device-authentication';
import { DeviceStatus } from '@shure/shared/angular/data-access/system-api/core';
import { mapDeviceModelFromSysApi } from '@shure/shared/angular/data-access/system-api/mappers';

import {
	SpectrumScannerDevice,
	SpectrumScannerResults,
	SpectrumScannerRoomDevice,
	SpectrumScanStatus
} from '../../api';
import {
	SpectrumScannerDeviceFragment,
	SpectrumScannerRoomDevicesFragment
} from '../graphql/generated/spectrum-scanner.generated';

export function mapSpectrumScannerDeviceFromSysApi(
	fragment: SpectrumScannerDeviceFragment,
	authenticated: boolean | false,
	readonlyModeSelected: boolean | false
): SpectrumScannerDevice {
	const {
		id,
		advertisedInterface,
		description,
		features: { name, dectRfSpectrumScan, room, rfBand, authentication }
	} = fragment;

	return {
		id,
		name: name?.name ?? '',
		model: mapDeviceModelFromSysApi(advertisedInterface.model ?? ''),
		roomId: room?.room?.id ?? '',
		scanStatus: {
			status: description.features.dectRfSpectrumScan.supported
				? mapSpectrumScannerDeviceStatusFromSysApi(fragment)
				: undefined,
			isMissing: !description.features.dectRfSpectrumScan.supported
		},
		results: {
			current: mapSpectrumScannerResult(dectRfSpectrumScan?.results.current),
			cumulative: mapSpectrumScannerResult(dectRfSpectrumScan?.results.cumulative)
		},
		rfBandNumber: rfBand?.band?.toLocaleUpperCase() ?? '',
		rfBandRange: {
			min: rfBand?.frequencyRange?.min ?? 0,
			max: rfBand?.frequencyRange?.max ?? 0
		},
		authentication: mapDeviceAuthenticationFromSysApi(authentication, authenticated, readonlyModeSelected)
	};
}

function mapSpectrumScannerResult(result?: {
	channelCounts: {
		highDensity: {
			aggressive: { max: number; min: number };
			conservative: { max: number; min: number };
		};
		standardDensity: {
			aggressive: { max: number; min: number };
			conservative: { max: number; min: number };
		};
	};
	rfAvailability: {
		high: number;
		moderate: number;
		low: number;
	};
}): SpectrumScannerResults {
	return {
		standardDensity: {
			strongEstimates: {
				max: result?.channelCounts.standardDensity.conservative.max ?? 0,
				min: result?.channelCounts.standardDensity.conservative.min ?? 0
			},
			maximumEstimates: {
				max: result?.channelCounts.standardDensity.aggressive.max ?? 0,
				min: result?.channelCounts.standardDensity.aggressive.min ?? 0
			}
		},
		highDensity: {
			strongEstimates: {
				max: result?.channelCounts.highDensity.conservative.max ?? 0,
				min: result?.channelCounts.highDensity.conservative.min ?? 0
			},
			maximumEstimates: {
				max: result?.channelCounts.highDensity.aggressive.max ?? 0,
				min: result?.channelCounts.highDensity.aggressive.min ?? 0
			}
		},
		rfAvailability: {
			high: result?.rfAvailability.high ?? 0,
			moderate: result?.rfAvailability.moderate ?? 0,
			no: result?.rfAvailability.low ?? 0
		}
	};
}

export function mapSpectrumScannerRoomDevicesFromSysApi(
	fragment: SpectrumScannerRoomDevicesFragment
): SpectrumScannerRoomDevice[] {
	return fragment.devices.map((device) => {
		return {
			id: device.id,
			scanSupported: device.description.features.dectRfSpectrumScan.supported
		};
	});
}

export function mapSpectrumScannerDeviceStatusFromSysApi(fragment: SpectrumScannerDeviceFragment): SpectrumScanStatus {
	const {
		features: { dectRfSpectrumScan }
	} = fragment;

	if (fragment.status !== DeviceStatus.Online) {
		return SpectrumScanStatus.Offline;
	}

	if (!dectRfSpectrumScan) {
		return SpectrumScanStatus.NotYetAvailable;
	} else if (dectRfSpectrumScan.inProgress) {
		return SpectrumScanStatus.Scanning;
	}
	return SpectrumScanStatus.NotScanning;
}
